/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

/**
 * TeamSection — founder bios with expandable highlights.
 */
import { useState } from "react";
import { motion } from "framer-motion";
import { Linkedin, ChevronDown, User } from "lucide-react";
import PitchSection from "../PitchSection";
import SectionLabel from "../SectionLabel";

export default function TeamSection({ data }) {
  const [expanded, setExpanded] = useState(null);

  return (
    <PitchSection id="team">
      <SectionLabel>{data.label}</SectionLabel>
      <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-12 leading-tight">
        {data.title}
      </h2>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {data.members.map((member, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: i * 0.15 }}
            className="bg-[#0F1826]/60 border border-white/8 rounded-2xl p-6 hover:border-white/15 transition-all group"
          >
            {/* Avatar */}
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#3B82F6]/20 to-[#4FA9A1]/20 border border-white/10 flex items-center justify-center mb-4 overflow-hidden">
              {member.photo ? (
                <img src={member.photo} alt={member.name} className="w-full h-full object-cover" />
              ) : (
                <User className="w-8 h-8 text-gray-500" />
              )}
            </div>

            <h3 className="text-white font-bold text-lg">{member.name}</h3>
            <p className="text-[#60A5FA] text-base mb-3">{member.role}</p>

            {/* LinkedIn */}
            {member.linkedin && (
              <a
                href={member.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-[#60A5FA] transition-colors mb-4"
              >
                <Linkedin className="w-3.5 h-3.5" />
                LinkedIn
              </a>
            )}

            {/* Highlights toggle */}
            <button
              onClick={() => setExpanded(expanded === i ? null : i)}
              className="flex items-center gap-1 text-sm text-gray-400 hover:text-white transition-colors mt-2"
            >
              Key highlights
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${expanded === i ? "rotate-180" : ""}`} />
            </button>

            {expanded === i && (
              <motion.ul
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                className="mt-3 space-y-2"
              >
                {member.highlights.map((h, j) => (
                  <li key={j} className="text-sm text-gray-400 leading-relaxed flex items-start gap-2">
                    <div className="w-1 h-1 rounded-full bg-[#4FA9A1] mt-1.5 flex-shrink-0" />
                    {h}
                  </li>
                ))}
              </motion.ul>
            )}
          </motion.div>
        ))}
      </div>
    </PitchSection>
  );
}
